import styled from '@emotion/styled';
import { format } from 'date-fns';
import React, { useCallback, useMemo } from 'react';
import { USER_DEFAULT_AVATAR } from 'shared/constants/links';
import { Player } from 'modules/players/domain/interfaces/player';

import { TeamPlayerAvatar } from '../team-card/styles';
import { theme } from 'styles/theme/theme';

export const SuggestedPlayerListContainer = styled.div`
  border: 0.5px solid ${theme.colors.SurfacePlatinum20};
  border-radius: 0 0 6px 6px;
  box-shadow:
    0 0 1px color-mix(in srgb, ${theme.colors.SurfaceRaisinBlack50} 8%, transparent),
    0 8px 16px color-mix(in srgb, ${theme.colors.SurfaceRaisinBlack50} 8%, transparent);
  cursor: pointer;
  position: absolute;
  width: 100%;
  margin-top: 4px;
`;

const SuggestedPlayerListItemContainer = styled.div`
  background-color: ${theme.colors.SurfaceWhite};
  padding: 10px;
  font-family: -apple-system, BlinkMacSystemFont, sans-serif;
  font-style: normal;
  font-weight: normal;
  font-size: 14px;
  line-height: 20px;
  display: flex;
  align-items: center;

  &:hover {
    background-color: ${theme.colors.SurfaceCultured10};
  }

  &:last-child {
    border-radius: 0 0 6px 6px;
  }
`;

const StyledPlayerName = styled.div`
  margin-left: 10px;
`;

const StyledPlayerBirthDate = styled.div`
  color: ${theme.colors.SurfaceRomanSilver30};
  margin-left: 10px;
`;

interface SuggestedPlayerListItemProps {
  user: Player;
  onClick: (user: Player) => void;
}

const SuggestedPlayerListItem = ({ user, onClick }: SuggestedPlayerListItemProps) => {
  const avatarUrl = useMemo(() => {
    const userAvatar = user.photo?.externalUrl;
    const tournamentAvatar = user.tournamentAvatars?.length ? (user.tournamentAvatars[0].image?.externalUrl ?? null) : null;

    return tournamentAvatar || userAvatar || USER_DEFAULT_AVATAR;
  }, [user.photo?.externalUrl, user.tournamentAvatars]);

  const birthDate = useMemo(() => {
    if (!user.birthDate) {
      return null;
    }

    return format(new Date(user.birthDate), 'dd.MM.uuuu');
  }, [user.birthDate]);

  const handleClick = useCallback(() => {
    onClick(user);
  }, [onClick, user]);

  return (
    <SuggestedPlayerListItemContainer onClick={handleClick}>
      <TeamPlayerAvatar url={avatarUrl} size={24} square />
      <StyledPlayerName>
        {user.surname} {user.name} {user.middleName}
      </StyledPlayerName>
      <StyledPlayerBirthDate>{birthDate}</StyledPlayerBirthDate>
    </SuggestedPlayerListItemContainer>
  );
};

interface SuggestedPlayerListProps {
  users: Player[];
  onClick: (user: Player) => void;
}

export const SuggestedPlayerList = React.forwardRef(
  ({ users, onClick }: SuggestedPlayerListProps, ref: React.ForwardedRef<HTMLDivElement>) => {
    if (!users.length) {
      return null;
    }

    return (
      <SuggestedPlayerListContainer ref={ref} tabIndex={0}>
        {users.map((user) => (
          <SuggestedPlayerListItem key={user.id} user={user} onClick={onClick} />
        ))}
      </SuggestedPlayerListContainer>
    );
  },
);
